import { inject, signal } from '@angular/core';

import { ErrorCatalog } from './error-catalog';
import type { IErrorNotifier } from './i-error-notifier';
import type { UserFacingError } from './user-facing-error.model';
import { TECHNICAL_LOG_HANDLER } from '../telemetry';

/** Quanto di un messaggio d'errore finisce nel diario: oltre, e' uno stack travestito da frase. */
const MAX_LOGGED_MESSAGE_LENGTH = 300;

/**
 * Quanto resta a vista l'avviso: il tempo di leggerlo, poi se ne va da solo. Non ha una X — chiuderlo a
 * mano sarebbe un gesto in piu' per una cosa che comunque non si puo' correggere da li'.
 */
export const ERROR_NOTICE_DURATION_MS = 5000;

export class ErrorNotifier implements IErrorNotifier {
  private readonly technicalLog = inject(TECHNICAL_LOG_HANDLER);

  private readonly _current = signal<UserFacingError | null>(null);
  readonly current = this._current.asReadonly();

  private expiry: ReturnType<typeof setTimeout> | undefined;

  notify(error: unknown): void {
    // Qui passa ogni errore che il portale riconosce — quelli non catturati arrivano dal
    // `GlobalErrorHandler`, quelli gestiti da chi li notifica — quindi e' il posto da cui il diario
    // tecnico li vede tutti una volta sola. Registrarli anche nel gestore globale li conterebbe due
    // volte per lo stesso fatto.
    this.technicalLog.record({
      kind: 'error',
      message: describe(error),
      detail: { shown: true },
    });

    this._current.set(ErrorCatalog.describe(error));

    // Un errore nuovo riparte da capo: l'avviso resta i suoi secondi, non quelli avanzati dal precedente.
    clearTimeout(this.expiry);
    this.expiry = setTimeout(() => this.dismiss(), ERROR_NOTICE_DURATION_MS);
  }

  dismiss(): void {
    clearTimeout(this.expiry);
    this.expiry = undefined;
    this._current.set(null);
  }
}

/**
 * L'errore in una riga. Il nome e il messaggio e nient'altro: le proprieta' che un errore si porta
 * dietro possono essere una risposta HTTP intera — corpo compreso — e il diario esce dal browser dentro
 * una segnalazione.
 */
function describe(error: unknown): string {
  if (!(error instanceof Error)) return 'Unknown error';

  return `${error.name}: ${error.message}`.slice(0, MAX_LOGGED_MESSAGE_LENGTH);
}
